import type { BlockResponse } from '../api';
import { blockColor, blockLabel } from './blockStyles';

interface BlockLegendProps {
  blocks: ReadonlyArray<BlockResponse>;
  /** Absolute page number currently rendered; the legend reflects only this page's blocks. */
  currentPage: number;
}

/**
 * Compact legend decoding the structure overlay: one chip per distinct block type present on the
 * current page (colour swatch + Spanish label + count), sorted by frequency. Renders nothing when
 * the page has no blocks. Shares colours and labels with {@link BlocksOverlay} so the two never drift.
 */
export function BlockLegend({ blocks, currentPage }: BlockLegendProps): JSX.Element | null {
  const counts = new Map<string, number>();
  for (const block of blocks) {
    if (block.pageNumber !== currentPage) {
      continue;
    }
    const key = block.blockType.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  if (counts.size === 0) {
    return null;
  }

  const entries = [...counts.entries()].sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Leyenda de estructura OCR">
      {entries.map(([type, count]) => (
        <span
          key={type}
          className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white/90 px-1.5 py-0.5 text-[11px] text-slate-600 shadow-sm backdrop-blur"
        >
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: blockColor(type) }}
            aria-hidden="true"
          />
          {blockLabel(type)}
          <span className="font-mono text-slate-400">{count}</span>
        </span>
      ))}
    </div>
  );
}
